import { DEFAULT_TIMEZONE, isSupportedTimezone } from '../constants/timezones';

const pad = (value) => String(value).padStart(2, '0');

const safeZone = (timezone) =>
  isSupportedTimezone(timezone) ? timezone : DEFAULT_TIMEZONE;

/** Explode an instant into calendar parts as seen from `timezone`. */
const partsIn = (value, timezone) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: safeZone(timezone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return formatter.formatToParts(new Date(value)).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
};

/**
 * Format an instant (a DateTimeField such as created_at) as a date in the
 * user's timezone.
 */
export const formatInstantDate = (value, timezone) => {
  if (!value) return '';
  const { day, month, year } = partsIn(value, timezone);
  return `${day}/${month}/${year}`;
};

/** Format an instant as date and time in the user's timezone. */
export const formatInstant = (value, timezone) => {
  if (!value) return '';
  const { day, month, year, hour, minute } = partsIn(value, timezone);
  return `${day}/${month}/${year} ${hour}:${minute}`;
};

/**
 * Format a calendar date (a DateField such as hire_date or effective_date).
 *
 * Deliberately does NOT convert. `new Date('2026-08-18')` parses as UTC
 * midnight, so converting it to Monterrey would render 17 Aug and silently
 * move every hire date back a day. The string is read literally instead.
 */
export const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return '';
  return `${pad(day)}/${pad(month)}/${year}`;
};

/** Today's calendar date in the given timezone, as YYYY-MM-DD for date inputs. */
export const todayInTimezone = (timezone) => {
  const { day, month, year } = partsIn(new Date().toISOString(), timezone);
  return `${year}-${month}-${day}`;
};
